import {getFromHub, postToHub} from './networkActions';
import {formatTimeHM} from "../utils/dateTimeUtils";
import {store} from "../App";
import {groupExcursionsByState} from "../utils/cartUtils";
import { getTranslation } from '../utils/translationUtils';
import { TAC_TEMPLATE_TYPES } from '../constants/tacConstants';

export async function updateCart() {
    const cartElements = await getFromHub('prebooking/cart/v2');

    const cruiseEntries = (cartElements?.entries?.CruiseExcursion ?  cartElements.entries.CruiseExcursion : []);
    const tacEntries = (cartElements?.entries?.Tac ?  cartElements.entries.Tac : []);
    const cart = parseCartEntries([...cruiseEntries, ...tacEntries]);

    store.dispatch({
        type: "SET_CART",
        cart,
    });
}

// used
// when user logs in
// when page is reloaded (router mounted)
// when booking is made (payment finished)
// when booking is refunded
export async function updateBookings() {
    const bookingEntries = await getFromHub('prebooking/bookings');
    if(bookingEntries.error) {
        return;
    }
    const bookings = parseCartEntries(bookingEntries, true);

    store.dispatch({
        type: "SET_BOOKINGS",
        bookings,
    });
}

//--

export async function removeExcursionFromCart(itineraryId, excursionId) {
    await updateExcursionInCart(itineraryId, excursionId, "remove");
}

export async function moveExcursionToWaitlistInCart(itineraryId, excursionId) {
    await updateExcursionInCart(itineraryId, excursionId, "moveToWaitlist");
}

export async function moveExcursionToBookableInCart(itineraryId, excursionId) {
    await updateExcursionInCart(itineraryId, excursionId, "moveToBookable");
}

async function updateExcursionInCart(itineraryId, excursionId, action) {
    // get all passengers belonging to the excursion we want to update/remove
    const bookingId = store.getState().cart.excursions[itineraryId][excursionId].bookingInformation;
    const passengers = store.getState().cart.excursions[itineraryId][excursionId].passengers.map((passenger) => {
        return {
            bookingInformation: bookingId,
            passenger: passenger.id,
            action,
        }
    });

    // post the changes to the hub
    await postToHub('prebooking/cart', passengers);
    await updateCart();
}

export async function removeNotBookableEntriesFromCart() {
    // get all passengers for all excursions that are not bookable
    const notBookableEntries = store.getState().cart.notBookableEntries;
    let removeCartEntries = [];

    //create the array with booking informations and passenger ids that need to be removed
    Object.values(notBookableEntries.excursions).forEach(group => {
        group.forEach(excursion => {
            excursion.passengers.forEach(passenger => {
                removeCartEntries.push({
                    bookingInformation: excursion.bookingInformation,
                    passenger: passenger.id,
                    action: "remove",
                });
            })
        })
    });

    notBookableEntries.tac.forEach(tacEntry => {
        removeCartEntries.push({
            type: "Tac",
            id:tacEntry.id,
            action: "remove",
        });
    });

    await postToHub('prebooking/cart', removeCartEntries);
    await updateCart();
}

const parseCartEntries = (cartElements, separateByState = false) => {
    let totalPrice = 0;
    let nrItems = 0;
    let excursions = {};
    let tacEntries = {};
    let notBookableEntries = {excursions: {}, tac: []};
    const currencySymbol = cartElements.length > 0 ? cartElements[0].currencySymbol : "";

    cartElements.forEach((cartElement) => {
        if(cartElement.type === "CruiseExcursion") {
            ({excursions, nrExcursions: nrItems, totalPrice, notBookableEntries} = addExcursion(cartElement, excursions, nrItems, totalPrice, notBookableEntries, separateByState))
        }

        if(cartElement.type === "BookingPartTac" || cartElement.type === "Tac") {
            ({tacEntries, nrExcursions: nrItems, totalPrice, notBookableEntries} = addTacEntry(cartElement, tacEntries, nrItems, totalPrice, notBookableEntries))
        }

    });

    //flatten the notBookableEntries.excursions to an array
    notBookableEntries.excursions = groupExcursionsByState(notBookableEntries.excursions);

    return {
        totalPrice,
        currencySymbol,
        nrExcursions: nrItems,
        excursions,
        notBookableEntries,
        tacEntries,
    };
};

// booking parts in the shape of the cart in the store
// a part has no cart status, its state says what the status would (e.g. Waitlisted)
export function parseBookingParts(bookingParts) {
    const {tacEntries, ...cart} = parseCartEntries(bookingParts.map(part => ({...part, status: part.state})));
    return {...cart, tac: tacEntries};
}

function addExcursion(cartElement, excursions, nrExcursions, totalPrice, notBookableEntries, separateByState) {
    // only take the data we will need/use
    const itineraryId = cartElement.itinerary.id;
    const excursionId = cartElement.excursion.id;
    const passengerData = {
        ...cartElement.passenger,
        cartElementId: cartElement.id,
        price: cartElement.price,
        status: cartElement.status,
    };

    const excursionData = {
        excursionId,
        itineraryId,
        excursionName: getTranslation(cartElement.excursion.name),
        portName: getTranslation(cartElement.excursion.port.name),
        portCountry: getTranslation(cartElement.excursion.port.localizedCountryName),
        price: cartElement.price,
        passengers: [passengerData],
        bookingInformation: cartElement.bookingInformation,
        date: cartElement.itinerary.date,
        duration: cartElement.cruiseBookingInformation.duration ? cartElement.cruiseBookingInformation.duration : cartElement.excursion.duration,
        bookingCode: cartElement.excursion.bookingCode,
        fromTime: cartElement.fromTime?.substring(11),
        status: cartElement.status,
        initialStatus: cartElement.initialStatus,
        state: cartElement.state,
        included: cartElement.excursion.included ? true : false
    };

    // update price of all entries in cart (skip items that have status of "Waitlisted"
    if(cartElement.status !== "Waitlisted") {
        totalPrice += cartElement.price;
    }

    //check if excursion already exists
    const existingExcursion = separateByState ?
        excursions?.[itineraryId]?.[excursionId]?.[cartElement.state] :
        excursions?.[itineraryId]?.[excursionId];

    if(existingExcursion) {
        // excursion already exists, add the passenger and update the price of the excursion
        existingExcursion.price += cartElement.price;
        existingExcursion.passengers.push(passengerData);
    }
    else {
        // excursion does not yet exist, add it as new one
        // increment excursions count, then add the data to the right nested place, creating nesting as needed
        nrExcursions++;
        excursions[itineraryId] = excursions[itineraryId] || {};

        if(separateByState) {
            excursions[itineraryId][excursionId] = excursions[itineraryId][excursionId] || {};
            excursions[itineraryId][excursionId][cartElement.state] = excursionData;
        }
        else {
            excursions[itineraryId][excursionId] = excursionData;
        }
    }

    //if the status is not bookable, add the element to the notBookableEntries
    if((cartElement.initialStatus === undefined && cartElement.status !== "Bookable") || cartElement.initialStatus !== cartElement.status) {
        const notBookableExcursionData = {
            excursionId,
            itineraryId,
            excursionName: cartElement.excursion.name.defaultValue,
            portName: getTranslation(cartElement.excursion.port.name),
            portCountry: getTranslation(cartElement.excursion.port.localizedCountryName),
            price: cartElement.price,
            passengers: [passengerData],
            bookingInformation: cartElement.bookingInformation,
            status: cartElement.status,
            initialStatus: cartElement.initialStatus,
            state: cartElement.state,
        };

        if (!notBookableEntries.excursions.hasOwnProperty(itineraryId)) {
            // itinerary entry does not yet exist, create it... and add add excursion data
            notBookableEntries.excursions[itineraryId] = {};
            notBookableEntries.excursions[itineraryId][excursionId] = notBookableExcursionData;
        }
        else {
            if (notBookableEntries.excursions[itineraryId].hasOwnProperty(excursionId)) {
                // excursion already exists, add the passenger and update the price of the excursion
                notBookableEntries.excursions[itineraryId][excursionId].price += cartElement.price;
                notBookableEntries.excursions[itineraryId][excursionId].passengers.push(passengerData);
            }
            else {
                // excursion does not exist yet, add it
                notBookableEntries.excursions[itineraryId][excursionId] = notBookableExcursionData;
            }
        }
    }

    return {
        excursions,
        nrExcursions,
        totalPrice,
        notBookableEntries,
    }
}

function addTacEntry(cartElement, tacEntries, nrExcursions, totalPrice, notBookableEntries) {
    // TODO - check with Christoph if we can unify this between cart and bookings
    const date = cartElement.fromDate ? cartElement.fromDate : cartElement.fromTime.substring(0,10);
    const time = cartElement.fromDate ? cartElement.fromTime : formatTimeHM(cartElement.fromTime);

    if(!tacEntries.hasOwnProperty(date)) {
        tacEntries[date] = [];
    }

    const specialRequirements = (cartElement.specialRequirements && cartElement.specialRequirements[0]) ? cartElement.specialRequirements : [];
    const tacEntryData = {
        id: cartElement.id,
        serviceProfileId: cartElement.serviceProfile.id,
        name: getTranslation(cartElement.serviceProfile.name),
        time,
        nrAdults: cartElement.numberOfAdults,
        nrChildren: cartElement.numberOfChildren,
        price: cartElement.price,
        sessionType: cartElement.sessionType || "",
        specialRequirements,
        serviceProfileTemplateType: cartElement.serviceProfile.templateType || TAC_TEMPLATE_TYPES.RESTAURANT,
        status: cartElement.status
    }
    //add the tac reservation info
    tacEntries[date].push(tacEntryData);

    // increment the total price
    totalPrice += cartElement.price;
    nrExcursions++;

    //if the status is not bookable, add the element to the notBookableEntries
    if(cartElement.status !== "Bookable") {
        notBookableEntries.tac.push(tacEntryData);
    }

    return {
        tacEntries,
        nrExcursions,
        totalPrice,
        notBookableEntries,
    };
}

export async function refundBooking(bookingIds, paymentReference) {
    const clientDomainUrl = window.location.protocol + "//" + window.location.host;

    const refundData = {
        paymentReference,
        ids: bookingIds,
        clientDomainUrl,
    };

    const response = await postToHub("prebooking/order/cancel", refundData);
    return response;
}

export function getCurrentCartFromStore() {
    return store.getState().cart;
}